import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { Action } from '@/services/auth/capabilities';

// Cosmetic only: the backend enforces the real roles, and useCan honours these overrides only
// for users who really hold admin.dev-panel.
interface DevOverridesState {
  overrides: Partial<Record<Action, boolean>>;
  previewEmbedded: boolean;
}

interface DevOverridesActions {
  // value === undefined clears the override (back to policy default).
  setOverride: (action: Action, value: boolean | undefined) => void;
  reset: () => void;
  setPreviewEmbedded: (value: boolean) => void;
}

type DevOverridesStore = DevOverridesState & DevOverridesActions;

export const useDevOverridesStore = create<DevOverridesStore>()(
  devtools(
    persist(
      (set) => ({
        overrides: {},
        previewEmbedded: false,
        setOverride: (action, value) =>
          set(
            (s) => {
              const next = { ...s.overrides };
              if (value === undefined) delete next[action];
              else next[action] = value;
              return { overrides: next };
            },
            false,
            'setOverride'
          ),
        reset: () => set({ overrides: {} }, false, 'reset'),
        setPreviewEmbedded: (previewEmbedded) =>
          set({ previewEmbedded }, false, 'setPreviewEmbedded'),
      }),
      { name: 'dev-capability-overrides' }
    ),
    { name: 'DevOverridesStore', enabled: import.meta.env.DEV }
  )
);

/** Single-action override lookup for useCan; undefined = no override in effect. */
export const useDevOverride = (action: Action): boolean | undefined =>
  useDevOverridesStore((s) => s.overrides[action]);

export const useDevOverrides = () => useDevOverridesStore((s) => s.overrides);

// Dev builds only. The preview hides the nav bar, and outside dev nothing else opens the dev
// panel, so the preview could never be switched off.
export const usePreviewEmbedded = (): boolean =>
  useDevOverridesStore((s) => import.meta.env.DEV && s.previewEmbedded);
